import React, { useEffect, useState } from 'react'

import { specificGuestLookup } from '../../../utilities/api'
import {
  EMPTY_DISPLAY_VALUE,
  formatCurrency,
  formatDateTime,
  formatNumber,
  getRoyalties,
  resaleOrderWasPurchased,
} from '../../../utilities/helpers'

import Badge from 'react-bootstrap/Badge'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'

import OrderSummaryView from '../../OrderSummaryView/OrderSummaryView'
import DetailList from './DetailList'
import {
  buyerName,
  listingFor,
  listingQuantity,
  orderStatus,
  ticketType,
} from './resaleOrderDisplay'

export default function ResaleOrderDetailsModal({ order, organizationUUID }) {
  const [ticketState, setTicketState] = useState({
    loading: true,
    error: null,
    data: null,
  })
  const badge = orderStatus(order)
  const purchased = resaleOrderWasPurchased(order)
  const name = buyerName(order)

  useEffect(() => {
    let cancelled = false

    if (!order?.id || !organizationUUID) {
      setTicketState({
        loading: false,
        error: order?.id ? 'Tickets are unavailable for this order.' : null,
        data: null,
      })
      return undefined
    }

    const load = async () => {
      setTicketState({ loading: true, error: null, data: null })
      try {
        const res = await specificGuestLookup({
          orderId: order.id,
          organizationUUID,
        })
        if (!cancelled) {
          setTicketState({ loading: false, error: null, data: res.data })
        }
      } catch (err) {
        console.error(err)
        if (!cancelled) {
          setTicketState({
            loading: false,
            error: 'Could not load tickets for this order.',
            data: null,
          })
        }
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [order?.id, organizationUUID])

  return (
    <>
      <Modal.Header closeButton className="border-0 pb-0">
        <Stack>
          <Modal.Title as="h4" className="h3 fw-bold">
            Order Details
          </Modal.Title>
          <div className="text-muted mt-1">
            Order ID: {order?.orderId || EMPTY_DISPLAY_VALUE}
          </div>
        </Stack>
      </Modal.Header>
      <Modal.Body>
        <DetailList
          rows={[
            {
              label: 'Status',
              value: badge ? (
                <Badge bg="none" className={`badge-sm badge--${badge.value}`}>
                  {badge.label}
                </Badge>
              ) : (
                EMPTY_DISPLAY_VALUE
              ),
            },
            { label: 'Buyer', value: name || EMPTY_DISPLAY_VALUE },
            { label: 'Email', value: order?.email || EMPTY_DISPLAY_VALUE },
            order?.phoneNumber
              ? { label: 'Phone', value: order.phoneNumber }
              : null,
            {
              label: 'Ticket type',
              value: ticketType(order) || EMPTY_DISPLAY_VALUE,
            },
            {
              label: 'Price',
              value: formatCurrency(listingFor(order)?.askingPrice),
            },
            { label: 'Quantity', value: formatNumber(listingQuantity(order)) },
            {
              label: 'Royalties',
              value: formatCurrency(purchased ? getRoyalties(order) : 0),
            },
            {
              label: 'Purchased',
              value: (
                <Badge
                  bg="none"
                  className={`badge-sm badge--${purchased ? 'paid' : 'released'}`}
                >
                  {purchased ? 'Yes' : 'No'}
                </Badge>
              ),
            },
            { label: 'Order total', value: formatCurrency(order?.total) },
            {
              label: 'Processed',
              value: formatDateTime(order?.processedAt) || EMPTY_DISPLAY_VALUE,
            },
          ]}
        />
        {ticketState.loading ? (
          <p className="text-muted mb-0 mt-3">Loading tickets…</p>
        ) : ticketState.error ? (
          <p className="text-danger mb-0 mt-3">{ticketState.error}</p>
        ) : ticketState.data ? (
          <div className="mt-4">
            <OrderSummaryView order={ticketState.data} />
          </div>
        ) : null}
      </Modal.Body>
    </>
  )
}
